/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Glossary / TermBase Term Card Component (Task 10.5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { Plus, AlertCircle, ArrowRight } from 'lucide-react';
import { useTranslation } from '../../i18n';

export interface GlossaryTerm {
  id: string;
  sourceTerm: string;
  targetTerm: string;
  domain: string;
  forbiddenTerms?: string[];
  notes?: string;
}

interface GlossaryCardProps {
  term: GlossaryTerm;
  onInsertTerm: (targetTerm: string) => void;
  isUsedInTarget?: boolean;
}

export const GlossaryCard: React.FC<GlossaryCardProps> = ({
  term,
  onInsertTerm,
  isUsedInTarget = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="p-3 rounded-lg border border-surface-border bg-surface hover:border-primary/40 transition-all duration-150 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-xs text-foreground">
              {term.sourceTerm}
            </span>
            <ArrowRight className="size-3 text-foreground-muted/60" />
            <span className="font-semibold text-xs text-emerald-500">
              {term.targetTerm}
            </span>
          </div>

          <div className="flex items-center gap-2 text-[10px] text-foreground-muted">
            <span className="px-1.5 py-0.2 rounded bg-surface-elevated border border-surface-border font-medium">
              {term.domain}
            </span>
            {isUsedInTarget && (
              <span className="text-emerald-500 font-medium">
                ✓ {t('cat.termApplied')}
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => onInsertTerm(term.targetTerm)}
          className="px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 text-[10px] font-semibold flex items-center gap-1 transition-colors flex-shrink-0"
          title={t('cat.insertTermTooltip')}
        >
          <Plus className="size-3" />
          <span>{t('cat.insertTerm')}</span>
        </button>
      </div>

      {term.notes && (
        <p className="text-[11px] text-foreground-muted italic">
          {term.notes}
        </p>
      )}

      {term.forbiddenTerms && term.forbiddenTerms.length > 0 && (
        <div className="pt-1.5 border-t border-surface-border/60 flex items-center gap-1.5 text-[10px] text-rose-500">
          <AlertCircle className="size-3 flex-shrink-0" />
          <span>
            {t('cat.forbiddenTermsLabel')}: {term.forbiddenTerms.join(', ')}
          </span>
        </div>
      )}
    </div>
  );
};
